import React, { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Grid,
  Box,
  Flex,
  FormControl,
  Input,
  SingleComboBox,
  Tooltip,
  InputCheckBox
} from '@itandi/itandi-bb-ui';
import { DealSearchCondition } from '~/models/Deal';
import { CompanyVendor, convertToComboBoxOption } from '~/models/CompanyVendor';
import { useSubtypes } from '~/hooks/useSubtypes';
import { DealTypeCheckBox } from './detail/DealTypeCheckBox';
import { SearchConditionDate } from './detail/SearchConditionDate';
import { CompanyStoreCombobox } from './detail/CompanyStoreCombobox';
import { StaffCombobox } from './detail/StaffCombobox';
import useItandiAccount from '~/hooks/useItandiAccount';
import { StatusCombobox } from './detail/StatusCombobox';
import { useStatuses } from '~/hooks/useStatuses';
import { ApplicationTypeCheckBox } from '~/components/specifics/deals/index/search/detail/ApplicationTypeCheckBox';

type Props = {
  companyVendors: CompanyVendor[];
  selectedCompanyVendor: CompanyVendor | null;
  dealSearchCondition: DealSearchCondition;
};

export const DealSearchDetailTab: React.FC<Props> = ({
  companyVendors,
  selectedCompanyVendor,
  dealSearchCondition
}: Props) => {
  const { data: subtypes } = useSubtypes().useIndex();
  const { register, setValue } = useFormContext<DealSearchCondition>();
  const defaultPrioritySort = dealSearchCondition.prioritySort ?? true;
  const [isPrioritySort, setIsPrioritySort] =
    React.useState(defaultPrioritySort);
  const { data: session } = useItandiAccount().currentAccount();
  const { data: statuses } = useStatuses().index();
  const onChangeCompanyVendor = React.useCallback(
    async (
      option: ComponentProps<typeof SingleComboBox>['value'] | null
    ): Promise<void> => {
      setValue('companyVendorId', option ? Number(option.value) : undefined);
    },
    [setValue]
  );
  if (subtypes === null || session === null || statuses === null) {
    return null;
  }
  return (
    <Box margin={16}>
      <Grid gap={8}>
        <FormControl label="未読を優先表示する" variant="Vertical">
          <InputCheckBox
            checked={isPrioritySort}
            labelText="未読を優先表示する"
            onChange={(e): void => {
              setValue('prioritySort', e.target.checked);
              setIsPrioritySort(e.target.checked);
            }}
          />
        </FormControl>
        <FormControl label="状況(ステータス)" variant="Vertical">
          <StatusCombobox
            dealSearchCondition={dealSearchCondition}
            statuses={statuses}
          />
        </FormControl>
        <FormControl label="状況(申請関連)" variant="Vertical">
          <Flex marginY={8} wrap="wrap">
            <ApplicationTypeCheckBox
              key="cancel"
              applicationType="cancel"
              dealSearchCondition={dealSearchCondition}
              labelText="取下申請中"
            />
            <ApplicationTypeCheckBox
              key="requestEstimation"
              applicationType="requestEstimation"
              dealSearchCondition={dealSearchCondition}
              labelText="見積申請あり"
            />
            <ApplicationTypeCheckBox
              key="requestEstimationByCompany"
              applicationType="requestEstimationByCompany"
              dealSearchCondition={dealSearchCondition}
              labelText="見積申請中"
            />
            <Flex alignItems="center">
              <Tooltip>
                施工会社による見積入力をスキップする案件のうち、見積変更を「申請中」の条件で絞り込みます
              </Tooltip>
            </Flex>
            <ApplicationTypeCheckBox
              key="draftEstimationByCompany"
              applicationType="draftEstimationByCompany"
              dealSearchCondition={dealSearchCondition}
              labelText="見積編集中"
            />
            <Flex alignItems="center">
              <Tooltip>
                施工会社による見積入力をスキップする案件のうち、見積変更を「編集中」(未申請)の条件で絞り込みます
              </Tooltip>
            </Flex>
          </Flex>
        </FormControl>
        <FormControl label="案件タイプ" variant="Vertical">
          <Flex marginY={8} wrap="wrap">
            <DealTypeCheckBox
              key="restoration"
              dealSearchCondition={dealSearchCondition}
              dealType="restoration"
              labelText="原状回復"
            />
            <DealTypeCheckBox
              key="repair"
              dealSearchCondition={dealSearchCondition}
              dealType="repair"
              labelText="修繕"
            />
            {subtypes.map((subtype) => (
              <DealTypeCheckBox
                key={subtype.id}
                dealSearchCondition={dealSearchCondition}
                dealSubtypeId={subtype.id}
                dealType="other"
                labelText={subtype.name}
              />
            ))}
          </Flex>
        </FormControl>
        <FormControl label="案件名" variant="Vertical">
          <Input {...register('name')} placeholder="案件名" />
        </FormControl>
        <FormControl label="所在地" variant="Vertical">
          <Input {...register('address')} placeholder="所在地" />
        </FormControl>
        <FormControl label="入居者" variant="Vertical">
          <Input {...register('tenantName')} placeholder="入居者" />
        </FormControl>
        {session.store?.masterAccount && (
          <FormControl label="店舗" variant="Vertical">
            <CompanyStoreCombobox dealSearchCondition={dealSearchCondition} />
          </FormControl>
        )}
        <FormControl label="担当者" variant="Vertical">
          <StaffCombobox dealSearchCondition={dealSearchCondition} />
        </FormControl>
        <FormControl label="施工会社" variant="Vertical">
          <SingleComboBox
            defaultValue={
              selectedCompanyVendor
                ? convertToComboBoxOption(selectedCompanyVendor)
                : null
            }
            onChange={onChangeCompanyVendor}
            options={companyVendors.map((c: CompanyVendor) =>
              convertToComboBoxOption(c)
            )}
            placeholder="施工会社"
          />
        </FormControl>
        <FormControl label="更新日" variant="Vertical">
          <Flex alignItems="center">
            <SearchConditionDate
              attributeName="updatedMinDate"
              defaultValue={dealSearchCondition.updatedMinDate}
            />
            〜
            <SearchConditionDate
              attributeName="updatedMaxDate"
              defaultValue={dealSearchCondition.updatedMaxDate}
            />
          </Flex>
        </FormControl>
        <FormControl label="立会日" variant="Vertical">
          <Flex alignItems="center">
            <SearchConditionDate
              attributeName="inspectionMinDate"
              defaultValue={dealSearchCondition.inspectionMinDate}
            />
            〜
            <SearchConditionDate
              attributeName="inspectionMaxDate"
              defaultValue={dealSearchCondition.inspectionMaxDate}
            />
          </Flex>
        </FormControl>
        <FormControl label="工期開始日" variant="Vertical">
          <Flex alignItems="center">
            <SearchConditionDate
              attributeName="constructionMinStartDate"
              defaultValue={dealSearchCondition.constructionMinStartDate}
            />
            〜
            <SearchConditionDate
              attributeName="constructionMaxStartDate"
              defaultValue={dealSearchCondition.constructionMaxStartDate}
            />
          </Flex>
        </FormControl>
        <FormControl label="工期終了日" variant="Vertical">
          <Flex alignItems="center">
            <SearchConditionDate
              attributeName="constructionMinEndDate"
              defaultValue={dealSearchCondition.constructionMinEndDate}
            />
            〜
            <SearchConditionDate
              attributeName="constructionMaxEndDate"
              defaultValue={dealSearchCondition.constructionMaxEndDate}
            />
          </Flex>
        </FormControl>
        <FormControl label="登録日" variant="Vertical">
          <Flex alignItems="center">
            <SearchConditionDate
              attributeName="registrationMinDate"
              defaultValue={dealSearchCondition.registrationMinDate}
            />
            〜
            <SearchConditionDate
              attributeName="registrationMaxDate"
              defaultValue={dealSearchCondition.registrationMaxDate}
            />
          </Flex>
        </FormControl>
      </Grid>
    </Box>
  );
};
